/**
 * Provider des overlays globaux : notifications et messages.
 * L'authentification se fait via les pages dédiées /connexion et /inscription.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationsDialog } from './NotificationsDialog';
import { MessagesDialog } from './MessagesDialog';
import { ROUTES } from '../../config/routes';

interface GlobalOverlaysContextValue {
  openNotifications: () => void;
  openMessages: () => void;
}

const GlobalOverlaysContext = createContext<GlobalOverlaysContextValue | null>(null);

export function GlobalOverlaysProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);

  const openNotifications = useCallback(() => setNotificationsOpen(true), []);
  const openMessages = useCallback(() => setMessagesOpen(true), []);

  const value = useMemo(
    () => ({ openNotifications, openMessages }),
    [openNotifications, openMessages],
  );

  return (
    <GlobalOverlaysContext.Provider value={value}>
      {children}
      <NotificationsDialog
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        onNavigateToCalls={() => {
          setNotificationsOpen(false);
          navigate(ROUTES.pro.calls);
        }}
      />
      <MessagesDialog
        open={messagesOpen}
        onClose={() => setMessagesOpen(false)}
        onNavigateToCalls={() => {
          setMessagesOpen(false);
          navigate(ROUTES.pro.calls);
        }}
      />
    </GlobalOverlaysContext.Provider>
  );
}

export function useGlobalOverlays(): GlobalOverlaysContextValue {
  const ctx = useContext(GlobalOverlaysContext);
  if (!ctx) throw new Error('useGlobalOverlays doit être utilisé dans <GlobalOverlaysProvider>');
  return ctx;
}
